import type {
  ConditionVersionInput,
  NegotiationVersion,
  TaxExpense,
} from "@/lib/adminNegotiationApi";

/** Componente de imposto/despesa no formulário: valores numéricos ficam como TEXTO durante a digitação. */
export interface TaxDraft {
  description: string;
  mode: "PERCENT" | "FIXO";
  percentValue: string;
  percentBase: string;
  fixedValue: string;
  fixedScope: string;
  fixedUnitId: string | null;
}

export interface FormState {
  id: string | null;
  version: number | null;
  validFrom: string;
  validTo: string;
  clientCommission: string;
  interPercent: string;
  interBase: string;
  target: string;
  minimum: string;
  taxListMode: string;
  taxes: TaxDraft[];
  note: string;
}

export function blankForm(isUnit: boolean): FormState {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: null, version: null, validFrom: today, validTo: "",
    clientCommission: "", interPercent: "", interBase: "",
    // defaults de margem 25/10 apenas no cliente; na unidade vazio = herdar
    target: isUnit ? "" : "25", minimum: isUnit ? "" : "10",
    taxListMode: isUnit ? "HERDAR" : "DEFINIDA", taxes: [], note: "",
  };
}

/** Número vindo do backend → texto editável no padrão BR (vírgula decimal). null/undefined → "". */
export function numberToInput(n: number | null | undefined): string {
  return n == null ? "" : n.toString().replace(".", ",");
}

export function toForm(v: NegotiationVersion): FormState {
  return {
    id: v.id, version: v.version, validFrom: v.validFrom, validTo: v.validTo ?? "",
    clientCommission: numberToInput(v.clientCommissionPercent),
    interPercent: numberToInput(v.intermediaryCommissionPercent),
    interBase: v.intermediaryCommissionBase ?? "",
    target: numberToInput(v.targetMarginPercent),
    minimum: numberToInput(v.minimumMarginPercent),
    taxListMode: v.taxListMode,
    taxes: v.taxes.map((t) => ({
      description: t.description,
      mode: t.mode,
      percentValue: numberToInput(t.percentValue),
      percentBase: t.percentBase ?? "PRECO_FINAL",
      fixedValue: numberToInput(t.fixedValue),
      fixedScope: t.fixedScope ?? "POR_PEDIDO",
      fixedUnitId: t.fixedUnitId,
    })),
    note: v.note ?? "",
  };
}

/**
 * Converte um decimal digitado (padrão BR) para número, SEM descartar separadores nem transformar
 * entrada inválida em outro número. Vazio → null (distinto de zero). Aceita vírgula decimal; aceita
 * ponto decimal quando não houver vírgula; rejeita formatos ambíguos/inválidos com mensagem clara.
 * Lança {@link Error} com rótulo do campo na entrada inválida (o chamador exibe e não envia).
 */
export function parseDecimalBR(raw: string, label: string): number | null {
  const t = (raw ?? "").trim();
  if (t === "") return null;
  const commas = (t.match(/,/g) ?? []).length;
  const dots = (t.match(/\./g) ?? []).length;
  if (commas > 0 && dots > 0) {
    throw new Error(`${label}: use apenas a vírgula decimal (ex.: 1,50). Formato ambíguo: "${raw}".`);
  }
  if (commas > 1 || dots > 1) {
    throw new Error(`${label}: há mais de um separador decimal em "${raw}".`);
  }
  const norm = commas === 1 ? t.replace(",", ".") : t;
  if (!/^\d+(\.\d+)?$/.test(norm)) {
    throw new Error(`${label}: número inválido "${raw}". Use dígitos e vírgula decimal (ex.: 1,50).`);
  }
  return Number(norm);
}

/** Converte um rascunho de imposto no componente da API (parse só aqui) saneando por modalidade. */
function draftToTax(d: TaxDraft): TaxExpense {
  const label = `"${d.description || "imposto/despesa"}"`;
  if (d.mode === "FIXO") {
    return {
      description: d.description, mode: "FIXO",
      percentValue: null, percentBase: null,
      fixedValue: parseDecimalBR(d.fixedValue, `Valor de ${label}`),
      fixedScope: d.fixedScope === "" ? null : (d.fixedScope as TaxExpense["fixedScope"]),
      fixedUnitId: d.fixedScope === "POR_PRODUTO" ? d.fixedUnitId : null,
      fixedUnitName: null,
    };
  }
  return {
    description: d.description, mode: "PERCENT",
    percentValue: parseDecimalBR(d.percentValue, `Percentual de ${label}`),
    percentBase: d.percentBase === "" ? null : (d.percentBase as TaxExpense["percentBase"]),
    fixedValue: null, fixedScope: null, fixedUnitId: null, fixedUnitName: null,
  };
}

/** Monta o payload convertendo os decimais no ENVIO. Lança {@link Error} (rótulo do campo) em entrada inválida. */
export function buildPayload(f: FormState): ConditionVersionInput | null {
  if (!f.validFrom) return null;
  return {
    validFrom: f.validFrom,
    validTo: f.validTo.trim() === "" ? null : f.validTo,
    clientCommissionPercent: parseDecimalBR(f.clientCommission, "Comissão do cliente"),
    intermediaryCommissionPercent: parseDecimalBR(f.interPercent, "Comissão do intermediário"),
    intermediaryCommissionBase: f.interBase === "" ? null : f.interBase,
    targetMarginPercent: parseDecimalBR(f.target, "Margem desejada"),
    minimumMarginPercent: parseDecimalBR(f.minimum, "Margem mínima"),
    taxListMode: f.taxListMode,
    taxes: f.taxListMode === "DEFINIDA" ? f.taxes.map(draftToTax) : [],
    note: f.note.trim() === "" ? null : f.note,
    expectedVersion: f.version,
  };
}
